import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  LinearProgress,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
// ...existing imports...
import EditIcon from "@mui/icons-material/Edit";
import "./style.css";
import { ZyppLiteApi } from "../../../API/Zypp-Lite-Api/LiteApi";
import { useNavigate } from "react-router-dom";
import { RentalPlan } from "../../../@Types/DRO/ZyppLiteApiResponse/ApiResponse";

const EvPlans = () => {
  // ...existing code...
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [rentalPlansData, setRentalPlansData] = useState<RentalPlan[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  // ...existing code...
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const tableHeaderCellStyle = {
    fontWeight: 600,
    fontSize: "14px",
    color: "#455A64",
  };

  const tableBodyCellStyle = {
    fontWeight: 400,
    fontSize: "14px",
    color: "#3b3b3b",
  };

  const rowsPerPage = 10;
  const pageRange = 10;
  const pageCount = Math.ceil(totalCount / rowsPerPage);

  const handlePageChange = (newPage: number) => {
    const newPageNumber = Math.max(1, Math.min(newPage + 1, pageCount));
    setPage(newPageNumber - 1);
  };

  const handleCreatePlan = () => {
    navigate("/evplans/create");
  };

  const handleEditPlan = (plan: RentalPlan) => {
    navigate("/evplans/create", { state: { editPlan: plan } });
  };

  const fetchRentalPlans = () => {
    setLoading(true);
    ZyppLiteApi.api
      ?.getRentalPlans()        
      .then((response) => {
        if (response.data?.rentalPlanDetails?.length > 0) {
          setRentalPlansData(response.data.rentalPlanDetails);
          setTotalCount(response.data.rentalPlanDetails.length);
        } else {
          setRentalPlansData([]);
          setTotalCount(0);
        }
      })
      .catch((error) => {
        console.error("API call error: ", error);
        setSnackbarMessage("Failed to fetch rental plans");
        setSnackbarSeverity("error");
        setShowErrorSnackbar(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchRentalPlans();
  }, [page]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const renderPageNumbers = () => {
    const pagesToShow = Math.min(pageCount, pageRange);
    const startPage = Math.max(
      0,
      Math.min(page - Math.floor(pageRange / 2), pageCount - pagesToShow)
    );

    return Array.from({ length: pagesToShow }, (_, index) => {
      const pageNumber = startPage + index + 1;
      return (
        <span
          key={pageNumber}
          className={pageNumber === page + 1 ? "active" : ""}
          onClick={() => handlePageChange(pageNumber - 1)}
        >
          {pageNumber}
        </span>
      );
    });
  };

  return (
    <div>
      <div className="flex flex-col bg-white min-h-80 box-shadow p-6 rounded-xl mx-16 mt-10">
        {/* Header */}
        <Grid container spacing={36} className="mb-4" alignItems="flex-start">
          <Grid item xs={12} md={4}>
            <h2 className="text-2xl font-semibold text-black">EV Plans</h2>
          </Grid>

          <Grid item xs={12} md={8} className="flex justify-end gap-4">

            {/* Create Plan Button */}
            <Button
              onClick={handleCreatePlan}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: "#00cd5b",
                borderRadius: "20px",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "#00b851",
                },
              }}
            >
              Create Plan
            </Button>
          </Grid>
        </Grid>

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeaderCellStyle}>Plan Name</TableCell>
                 <TableCell sx={tableHeaderCellStyle}>Plan Type</TableCell>
                <TableCell
                 sx={tableHeaderCellStyle}>Fleet Type</TableCell>
                <TableCell sx={tableHeaderCellStyle}>Validity in Days</TableCell>
                <TableCell sx={tableHeaderCellStyle}>Plan Amount</TableCell>
                <TableCell sx={tableHeaderCellStyle}>Validity in Distance</TableCell>
                <TableCell sx={tableHeaderCellStyle}>Created At</TableCell>
                <TableCell sx={tableHeaderCellStyle}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rentalPlansData.length > 0 ? (
                rentalPlansData.map((plan, index) => (
                  <TableRow key={plan.id || index}>
                    <TableCell sx={tableBodyCellStyle}>{plan.rentalPlanName || "-"}</TableCell>
                      <TableCell sx={tableBodyCellStyle}>{plan.planType || "-"}</TableCell>
                    <TableCell sx={tableBodyCellStyle}>{plan.fleetName || "-"}</TableCell>
                    <TableCell sx={tableBodyCellStyle}>{plan.validityInDays} Days</TableCell>
                    <TableCell sx={tableBodyCellStyle}>₹ {plan.rentAmount?.toLocaleString()}</TableCell>
                    <TableCell sx={tableBodyCellStyle}>{plan.validityInKm} KMs</TableCell>
                    <TableCell sx={tableBodyCellStyle}>{formatDate(plan.createdAt)}</TableCell>
                    <TableCell sx={tableBodyCellStyle}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditPlan(plan)}
                        sx={{ color: "#00cd5b" }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={tableBodyCellStyle}>
                    {loading ? "Loading..." : "No rental plans found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalCount > rowsPerPage && (
          <div className="pagination mt-4 flex justify-end items-center">
            <span
              className="unactive"
              onClick={() => handlePageChange(page - 1)}
              style={{ cursor: page === 0 ? "not-allowed" : "pointer", opacity: page === 0 ? 0.5 : 1 }}
            >
              {"<"}
            </span>
            {renderPageNumbers()}
            <span
              className="unactive"
              onClick={() => handlePageChange(page + 1)}
              style={{ cursor: page >= pageCount - 1 ? "not-allowed" : "pointer", opacity: page >= pageCount - 1 ? 0.5 : 1 }}
            >
              {">"}
            </span>
          </div>
        )}
      </div>

      {/* Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setShowErrorSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EvPlans;
